import { useParams, useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { Card } from '@/components/ui/card'
import CampaignDetail from '../components/roas-analysis-view'
import { formatPercent, formatUSD } from '@/lib/format-utils'
import NotesDrawer from '../components/notes-drawer'
import { useMemo, useState, useEffect } from 'react'
import Loader from '@/components/zthd/Loader'
import { toast } from 'sonner'
import { Copy, Search, X, Loader2 } from 'lucide-react'
import { obtainAppNameByPid } from '@/comm/app'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { fetcher } from '@/hooks'
import { useDatePicker2 } from '@/components/MyDatePicker'
import { troasQueryFromPicker } from '../troas-api-query'
import PredictionPanel from './components/prediction-panel'
import { RoasDatePickerTroasGate } from '../components/roas-date-picker-troas-gate'
import CampaignPlatformLink from '@/components/roas/campaign-platform-link'

const CampInfo = ({ id, onCompare, compareIds = [], isLoadingCompareData }) => {
  const { data: camp } = useSWR([`/campaigns/${id}`])
  const { data: compareCamps } = useSWR(
    compareIds.length > 0 ? ['compare-camps-basic', [...compareIds].sort().join(',')] : null,
    async ([, idsStr]) => {
      const ids = idsStr.split(',')
      return Promise.all(ids.map(cid => fetcher([`/campaigns/${cid}`])))
    },
    { keepPreviousData: true }
  )
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const { data: searchResults, isLoading: isSearching } = useSWR(
    debouncedSearchTerm ? [`/campaigns/search`, { term: debouncedSearchTerm }] : null,
    fetcher
  )

  // 过滤掉当前campaign并排序：state=0 优先，state!=0 在后
  const filteredSearchResults = useMemo(() => {
    if (!searchResults) return null
    const filtered = searchResults.filter(campaign => campaign.id !== id && !compareIds.includes(campaign.id))
    // 排序：state=0 在前，state!=0 在后
    return filtered.sort((a, b) => {
      const aState = a.state ?? 1
      const bState = b.state ?? 1
      if (aState === 0 && bState !== 0) return -1
      if (aState !== 0 && bState === 0) return 1
      return 0
    })
  }, [searchResults, id, compareIds])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm])

  const handleCopyCampLink = () => {
    const url = `https://monday.stickermobi.com/troas/campaigns/${camp.id}`
    // URL 后必须留空格再跟 )，否则粘贴到 Excel 后点击链接会把 ) 算入 URL 导致跳转失败
    navigator.clipboard.writeText(`[${camp.name}](${url} )`)
    toast(`Campaign Link ${camp.id} copied to clipboard`)
  }

  const handleSelectCompare = (campaign) => {
    if (compareIds.length >= 5) {
      toast.error('最多支持对比 5 个 Campaign')
      return
    }
    onCompare([...compareIds, campaign.id])
    setOpen(false)
    setSearchTerm('')
  }

  const handleRemoveCompare = (cid) => {
    onCompare(compareIds.filter(id => id !== cid))
  }

  const handleCompareCampaignClick = (cid) => {
    window.open(`/troas/campaigns/${cid}`, '_blank')
  }

  if (!camp) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="text-lg font-medium flex flex-wrap gap-4 items-center min-w-0">
        <span className="break-all">{camp.name}</span>
        <span>{camp.country}</span>
        <span>
          {obtainAppNameByPid(camp.pid)}-{camp.pid}
        </span>
        <Copy onClick={handleCopyCampLink} className="w-4 h-4 cursor-pointer" />
      </h3>

      <div className="flex flex-wrap items-center gap-2">
        <CampaignPlatformLink campaign={camp} />
        <div className="flex flex-wrap gap-2 items-center">
          {compareIds.map((cid, idx) => {
            const cInfo = compareCamps?.find(c => c.id === cid)
            return (
              <div key={cid} className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1 text-xs">
                {isLoadingCompareData ? (
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                ) : (
                  <button
                    onClick={() => handleCompareCampaignClick(cid)}
                    className="underline hover:no-underline cursor-pointer text-primary truncate max-w-[150px]"
                    title={cInfo?.name || cid}
                  >
                    {cInfo?.name || cid}
                  </button>
                )}
                <X 
                  className="w-3 h-3 cursor-pointer hover:text-destructive" 
                  onClick={() => handleRemoveCompare(cid)}
                />
              </div>
            )
          })}
          
          {compareIds.length < 5 && (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  {compareIds.length > 0 ? '+ Compare' : 'Compare'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="end">
                <div className="flex items-center border-b px-3">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <Input
                    className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-none focus-visible:ring-0"
                    placeholder="Search campaign..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="max-h-[300px] overflow-y-auto p-1">
                  {!debouncedSearchTerm && !isSearching && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      请输入 Campaign 名称或 Campaign ID 进行检索
                    </div>
                  )}
                  {isSearching && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      搜索中...
                    </div>
                  )}
                  {!isSearching && debouncedSearchTerm && filteredSearchResults?.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      未找到匹配的 Campaign
                    </div>
                  )}
                  {!isSearching && filteredSearchResults && filteredSearchResults.length > 0 && filteredSearchResults.map((campaign) => {
                    const isInactive = campaign.state !== 0
                    return (
                      <div
                        key={campaign.id}
                        className={`flex flex-col gap-1 rounded-sm px-2 py-1.5 text-sm cursor-pointer ${
                          isInactive 
                            ? 'bg-muted hover:bg-muted/80' 
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                        onClick={() => handleSelectCompare(campaign)}
                      >
                        <span>{campaign.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {campaign.id} - {campaign.country}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  )
}

// 从 URL 解析日期范围 "start,end"，返回 [start, end] 或 []
function parseDateRangeParam(str) {
  if (!str || typeof str !== 'string') return []
  const parts = str.split(',').map(s => s.trim()).filter(Boolean)
  if (parts.length !== 2) return []
  const [start, end] = parts
  const startDate = new Date(start)
  const endDate = new Date(end)
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return []
  if (startDate > endDate) return []
  return [start, end]
}

const Detail = () => {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [compareIds, setCompareIds] = useState(() => {
    const ids = searchParams.get('compare_ids')
    if (!ids) return []
    return ids.split(',').map(s => s.trim()).filter(Boolean)
  })
  const [compareDates, setCompareDates] = useState(() => {
    return parseDateRangeParam(searchParams.get('compare_dates'))
  })
  // 同 campaign 不同日期对比时，主区间 = Analyze 选定的目标时段（点击 Compare 时由 PredictionPanel 传入）
  const [mainDatesForCompare, setMainDatesForCompare] = useState(() => {
    return parseDateRangeParam(searchParams.get('main_dates'))
  })

  // 状态变化时同步到 URL（使用 window.location.search 以保留其他组件的参数）
  useEffect(() => {
    const next = new URLSearchParams(window.location.search)
    if (compareIds.length > 0) {
      next.set('compare_ids', compareIds.join(','))
    } else {
      next.delete('compare_ids')
    }
    if (compareDates.length === 2) {
      next.set('compare_dates', compareDates.join(','))
    } else {
      next.delete('compare_dates')
    }
    if (mainDatesForCompare.length === 2) {
      next.set('main_dates', mainDatesForCompare.join(','))
    } else {
      next.delete('main_dates')
    }
    const nextStr = next.toString()
    const currentStr = window.location.search.slice(1)
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true })
    }
  }, [compareIds, compareDates, mainDatesForCompare])

  return (
    <RoasDatePickerTroasGate isTotal={false}>
      <CampaignDetailPageBody
        id={id}
        compareIds={compareIds}
        setCompareIds={setCompareIds}
        compareDates={compareDates}
        setCompareDates={setCompareDates}
        mainDatesForCompare={mainDatesForCompare}
        setMainDatesForCompare={setMainDatesForCompare}
      />
    </RoasDatePickerTroasGate>
  )
}

/** 在 RoasDatePickerTroasGate 之后挂载，避免日期 store 未对齐就请求 /troas */
function CampaignDetailPageBody({
  id,
  compareIds,
  setCompareIds,
  compareDates,
  setCompareDates,
  mainDatesForCompare,
  setMainDatesForCompare,
}) {
  const pickerStart = useDatePicker2((s) => s.startDate)
  const pickerEnd = useDatePicker2((s) => s.endDate)
  const troasApiQuery = useMemo(
    () => troasQueryFromPicker(pickerStart, pickerEnd),
    [pickerStart, pickerEnd],
  )

  const {
    data: roasData,
    isLoading,
    isValidating,
  } = useSWR([`/troas/${id}`, troasApiQuery])

  const { data: notesData } = useSWR([`/campaigns/${id}/notes`])

  // 检查所有对比数据的加载状态
  const { isValidating: isValidatingCompare } = useSWR(
    compareIds?.length > 0
      ? [
          'compare-all-status',
          [...compareIds].sort().join(','),
          JSON.stringify(troasApiQuery),
        ]
      : null,
    async ([, idsStr]) => {
      const ids = idsStr.split(',')
      return Promise.all(
        ids.map(async (cid) => {
          return Promise.all([
            fetcher([`/troas/${cid}`, troasApiQuery]),
            fetcher([`/campaigns/${cid}`]),
            fetcher([`/campaigns/${cid}/prediction`]),
            fetcher([`/campaigns/${cid}/roas/90`]),
          ])
        }),
      )
    },
  )

  // 计算综合的加载状态：只要有一个数据在加载或验证中，就显示loading
  const isLoadingCompareData = useMemo(() => {
    if (compareIds.length === 0) return false
    return isValidatingCompare
  }, [compareIds, isValidatingCompare])

  const summary = useMemo(() => roasData?.summary ?? {}, [roasData])

  const notes = useMemo(
    () =>
      notesData?.map((n) => ({
        ...n,
        cid: id,
      })) ?? [],
    [notesData, id],
  )

  const isInitialLoading = isLoading && !roasData

  if (isInitialLoading) return <Loader>Loading...</Loader>

  return (
    <div className="space-y-4 py-4">
      <CampInfo
        id={id}
        onCompare={setCompareIds}
        compareIds={compareIds}
        isLoadingCompareData={isLoadingCompareData}
      />
      <PredictionPanel 
        campaignId={id}
        allDailyData={roasData?.dailyData}
        compareIds={compareIds}
        onCompareDates={(compare, main) => {
          setCompareDates(compare || [])
          setMainDatesForCompare(main && main.length === 2 ? main : [])
        }}
      />
      <Summary summary={summary} />
      <CampaignDetail
        id={id}
        compareIds={compareIds}
        compareDates={compareDates}
        mainDatesForCompare={mainDatesForCompare}
        onClearCompareDates={() => {
          setCompareDates([])
          setMainDatesForCompare([])
        }}
        prefetchedTroas={{
          data: roasData,
          isLoading,
          isValidating,
        }}
      />
      <NotesDrawer notes={notes} cid={id} />
    </div>
  )
}
const Summary = ({ summary }) => {
  if (!summary) return null
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between"></div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-sm text-muted-foreground">
            Revenue Last 30 Days
          </div>
          <div className="text-2xl font-bold">
            {formatUSD(summary.totalRevenueLast30Days)}
          </div>
          <div className="text-xs text-muted-foreground">Total Revenue</div>
          <div className="text-xxl">{formatUSD(summary.totalRevenue)}</div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">Cost Last 30 Days</div>
          <div className="text-2xl font-bold">
            {formatUSD(summary.totalCostLast30Days)}
          </div>
          <div className="text-xs text-muted-foreground">Total Cost</div>
          <div className="text-xxl">{formatUSD(summary.totalCost)}</div>
        </div>

        <div>
          <div className="text-sm text-muted-foreground">ROAS Last 30 Days</div>
          <div className="text-2xl font-bold">
            {formatPercent(
              (summary.totalRevenueLast30Days - summary.totalCostLast30Days) /
                summary.totalRevenueLast30Days,
            )}
          </div>
          <div className="text-xs text-muted-foreground">Total Roas</div>
          <div className="text-xxl">
            {formatPercent(
              (summary.totalRevenue - summary.totalCost) / summary.totalRevenue,
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default Detail
